// src/pages/admin/QuotesOverview.js
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../lib/firebase";

function money(n) {
  const x = Number(n || 0);
  return x.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString();
}

function getMillis(ts) {
  if (!ts) return 0;
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.getTime();
}

function statusLabel(s) {
  const v = (s || "draft").toLowerCase();
  if (v === "draft") return "Draft";
  if (v === "sent") return "Sent";
  if (v === "viewed") return "Viewed";
  if (v === "approved") return "Approved";
  if (v === "declined") return "Declined";
  return s || "Draft";
}

export default function QuotesOverview() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    const ref = collection(db, "quotes");

    // Simple single-field ordering (won't require composite index)
    const qy = query(ref, orderBy("updatedAt", "desc"), limit(300));

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setQuotes(rows);
        setLoading(false);
      },
      () => {
        setQuotes([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();

    let rows = [...quotes];

    if (status !== "all") {
      rows = rows.filter((r) => (r.status || "draft") === status);
    }

    if (needle) {
      rows = rows.filter((r) => {
        const blob = [
          r.quoteNumber,
          r.title,
          r.clientName,
          r.clientOrganization,
          r.clientEmail,
          r.clientPhone,
          r.clientAddress?.city,
          r.clientAddress?.region,
          r.clientAddress?.country,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return blob.includes(needle);
      });
    }

    // Safety sort if some docs have missing updatedAt
    rows.sort((a, b) => {
      const am = getMillis(a.updatedAt) || getMillis(a.createdAt);
      const bm = getMillis(b.updatedAt) || getMillis(b.createdAt);
      return bm - am;
    });

    return rows;
  }, [quotes, q, status]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <div>
          <h2 style={{ marginBottom: 6 }}>Quotes</h2>
          <div className="micro muted">
            {loading ? "Loading…" : `${filtered.length} shown • ${quotes.length} total`}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search quote #, client, org, email…"
            style={{
              width: 320,
              maxWidth: "100%",
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid rgba(0,0,0,0.15)",
              background: "rgba(255,255,255,0.75)",
            }}
          />

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid rgba(0,0,0,0.15)",
              background: "rgba(255,255,255,0.75)",
            }}
          >
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="viewed">Viewed</option>
            <option value="approved">Approved</option>
            <option value="declined">Declined</option>
          </select>

          <Link className="btn" to="/admin">
            Back
          </Link>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        {loading && <p className="muted">Loading quotes…</p>}

        {!loading && filtered.length === 0 && (
          <p className="muted">No quotes match your search.</p>
        )}

        {!loading && filtered.length > 0 && (
          <div style={{ display: "grid", gap: 12 }}>
            {filtered.map((r) => {
              const total = r?.totals?.grandTotal ?? 0;
              const updated = r.updatedAt || r.createdAt;
              const location = [
                r.clientAddress?.city,
                r.clientAddress?.region,
                r.clientAddress?.country,
              ]
                .filter(Boolean)
                .join(", ");

              const publicUrl = `${window.location.origin}/quote/${r.id}`;

              return (
                <article
                  key={r.id}
                  style={{
                    border: "1px solid rgba(0,0,0,0.1)",
                    borderRadius: 14,
                    padding: 14,
                    background: "rgba(255,255,255,0.7)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ minWidth: 260 }}>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
                        <strong style={{ fontSize: 16 }}>
                          {r.quoteNumber || "Quote"}
                        </strong>
                        <span className="micro muted">
                          {statusLabel(r.status)}
                        </span>
                        <span className="micro muted">•</span>
                        <strong style={{ fontSize: 16 }}>{money(total)}</strong>
                      </div>

                      <div className="micro muted" style={{ marginTop: 6 }}>
                        {r.clientName || "Client"}
                        {r.clientOrganization ? ` — ${r.clientOrganization}` : ""}
                      </div>

                      {(r.clientEmail || r.clientPhone) && (
                        <div className="micro muted" style={{ marginTop: 4 }}>
                          {[r.clientEmail, r.clientPhone].filter(Boolean).join(" • ")}
                        </div>
                      )}

                      {location && (
                        <div className="micro muted" style={{ marginTop: 4 }}>
                          {location}
                        </div>
                      )}

                      <div className="micro muted" style={{ marginTop: 8 }}>
                        Updated: {formatDate(updated)}
                        {r.sentAt ? ` • Sent: ${formatDate(r.sentAt)}` : ""}
                        {r.lastViewedAt ? ` • Viewed: ${formatDate(r.lastViewedAt)}` : ""}
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <Link className="btn primary" to={`/admin/quotes/${r.id}`}>
                        Edit
                      </Link>

                      <a className="btn" href={publicUrl} target="_blank" rel="noreferrer">
                        View Public
                      </a>

                      <button
                        type="button"
                        className="btn"
                        onClick={() => navigator.clipboard.writeText(publicUrl)}
                      >
                        Copy Link
                      </button>
                    </div>
                  </div>

                  {r.title && (
                    <div className="micro muted" style={{ marginTop: 10 }}>
                      {r.title}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
